// The calendar popover under the date row (Visual Direction, round 5b): the
// month centered between its arrows, Monday-first weeks, and each night in
// its Calendar / Day state. Tapping a night opens it; nights before the first
// and still to come are dim and not tappable. Entitled players see the grid
// alone, which is everyone until entitlements exist.
import { useEffect, useMemo, useState } from 'react';
import { formatFullDate } from '../../systems/clock/serverClock';
import {
  addMonths,
  calendarStore,
  dayState,
  formatMonth,
  loadCalendar,
  monthWeeks,
  WEEKDAY_LABELS,
  type DayContext,
  type DayState,
} from '../../systems/night/calendar';
import { useStore } from '../../systems/store';
import { IconButton } from '../components/Button';
import { Icon } from '../components/Icon';

interface Props {
  shown: string;
  tonight: string;
  firstNight: string;
  onPick: (date: string) => void;
}

const MARK_LABEL: Partial<Record<DayState, string>> = {
  selected: 'shown',
  tonight: 'tonight',
  all: 'all played',
  some: 'some played',
};

export function Calendar({ shown, tonight, firstNight, onPick }: Props) {
  const [month, setMonth] = useState(shown.slice(0, 7));
  const calendar = useStore(calendarStore);
  useEffect(() => void loadCalendar(month), [month]);

  const data = calendar.month === month ? calendar.data : null;
  const ctx: DayContext = useMemo(
    () => ({ shown, tonight, firstNight, marks: new Map(data?.nights.map((n) => [n.date, n.mark])) }),
    [shown, tonight, firstNight, data],
  );
  const previous = month > firstNight.slice(0, 7) ? addMonths(month, -1) : null;
  const next = month < tonight.slice(0, 7) ? addMonths(month, 1) : null;

  return (
    <div className="panel calendar" role="dialog" aria-label="Pick a night">
      <div className="calendar-head">
        <IconButton icon="chevron-left" label="Previous month" className="calendar-arrow" disabled={!previous} onClick={() => previous && setMonth(previous)} />
        <span className="calendar-month">{formatMonth(month)}</span>
        <IconButton icon="chevron-right" label="Next month" className="calendar-arrow" disabled={!next} onClick={() => next && setMonth(next)} />
      </div>
      <div className="calendar-grid">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="calendar-weekday label-mono" aria-hidden="true">
            {label}
          </span>
        ))}
        {monthWeeks(month)
          .flat()
          .map((date, i) => (date ? <Day key={date} date={date} state={dayState(date, ctx)} onPick={onPick} /> : <span key={`pad-${i}`} />))}
      </div>
      {calendar.error && calendar.month === month && <p className="calendar-error quiet">{calendar.error}</p>}
    </div>
  );
}

function Day({ date, state, onPick }: { date: string; state: DayState; onPick: (date: string) => void }) {
  const day = Number(date.slice(8));
  const mark = MARK_LABEL[state];
  if (state === 'closed') {
    return (
      <span className="calendar-day" data-state={state} aria-disabled="true">
        <span className="calendar-day-number">{day}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className="calendar-day"
      data-state={state}
      aria-label={mark ? `${formatFullDate(date)}, ${mark}` : formatFullDate(date)}
      aria-current={state === 'selected' ? 'date' : undefined}
      onClick={() => onPick(date)}
    >
      {state === 'all' ? <Icon name="star" className="calendar-day-star" /> : <span className="calendar-day-number">{day}</span>}
      {state === 'some' && <span className="calendar-day-ring" aria-hidden="true" />}
    </button>
  );
}
